import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Shield, Users, Calendar, MessageSquare, TrendingUp, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function RepDashboard() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [proposals, setProposals] = useState({ sent: [], received: [] });
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [t, p, f, mp, inv] = await Promise.all([
        api.get("/me/teams"),
        api.get("/players"),
        api.get("/fixtures?status=upcoming"),
        api.get("/me/match-proposals").catch(() => ({ data: { sent: [], received: [] } })),
        api.get("/me/invites").catch(() => ({ data: [] })),
      ]);
      setTeams(t.data || []);
      setPlayers(p.data || []);
      setFixtures(f.data || []);
      setProposals(mp.data || { sent: [], received: [] });
      setInvites(inv.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  const stats = useMemo(() => {
    const myTeamIds = new Set(teams.map((t) => t.id));
    const rosterTotal = players.filter((p) => myTeamIds.has(p.team_id)).length;
    const myFixtures = fixtures.filter((f) => myTeamIds.has(f.home_team_id) || myTeamIds.has(f.away_team_id));
    return {
      teams: teams.length,
      roster: rosterTotal,
      upcoming: myFixtures.length,
      pendingProposals: (proposals.received || []).length,
      pendingInvites: (invites || []).length,
      sentProposals: (proposals.sent || []).length,
    };
  }, [teams, players, fixtures, proposals, invites]);

  if (user === null) return <div className="text-gray-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  if (loading) {
    return <div className="text-gray-500 font-mono text-xs uppercase tracking-widest">Loading your rep dashboard…</div>;
  }

  if (teams.length === 0) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 text-center py-16" data-testid="rep-dashboard-empty">
        <Shield className="w-12 h-12 mx-auto text-gray-600" />
        <div className="font-heading text-3xl font-black uppercase tracking-tight">No teams yet</div>
        <p className="text-sm text-gray-400 font-mono">You aren't listed on any team as owner or rep.</p>
        <Link to="/my-team" data-testid="rep-dashboard-cta" className="inline-block text-xs font-mono uppercase tracking-widest bg-cyan-400 hover:bg-cyan-300 text-black px-4 py-2 font-bold">
          Register a team →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rep-dashboard">
      {/* Hero */}
      <div className="border-l-4 border-[#00FFB2] pl-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#00FFB2]">Rep Dashboard</div>
        <div className="font-heading text-4xl font-black uppercase tracking-tight">
          {user.name || (user.email || "").split("@")[0]}
        </div>
        <p className="text-xs font-mono text-gray-500 mt-1">
          One board across every team you rep or own — schedule at a glance, proposals to sign off, rosters to keep honest.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="rep-dashboard-kpis">
        {[
          { key: "teams", label: "Teams", val: stats.teams, color: "#00F0FF", icon: Shield },
          { key: "roster", label: "Roster total", val: stats.roster, color: "#F4D03F", icon: Users },
          { key: "upcoming", label: "Upcoming matches", val: stats.upcoming, color: "#B084FF", icon: Calendar },
          { key: "pending-proposals", label: "Proposals to sign", val: stats.pendingProposals, color: "#00FFB2", icon: MessageSquare },
          { key: "sent-proposals", label: "Awaiting opp", val: stats.sentProposals, color: "#FF007F", icon: TrendingUp },
          { key: "pending-invites", label: "Rep invites", val: stats.pendingInvites, color: "#7DF9FF", icon: Shield },
        ].map(({ key, label, val, color, icon: Icon }) => (
          <div
            key={key}
            data-testid={`rep-dashboard-kpi-${key}`}
            className="border-l-2 bg-[#0A0A10] p-3"
            style={{ borderLeftColor: color }}
          >
            <div className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5" style={{ color }} />
              <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500">{label}</div>
            </div>
            <div className="font-heading font-black text-3xl mt-1" style={{ color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Alerts strip — pending proposals */}
      {stats.pendingProposals > 0 && (
        <Link
          to="/reps"
          data-testid="rep-dashboard-proposals-alert"
          className="block border border-[#00FFB2] bg-[#00FFB2]/10 hover:bg-[#00FFB2]/20 p-3 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#00FFB2] animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-widest text-[#00FFB2] font-bold">
                {stats.pendingProposals} match proposal{stats.pendingProposals > 1 ? "s" : ""} awaiting your sign-off
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-[#00FFB2]" />
          </div>
        </Link>
      )}

      {/* Team cards */}
      <section className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
          Your teams — {teams.length}
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="rep-dashboard-team-grid">
          {teams.map((t) => {
            const roster = players.filter((p) => p.team_id === t.id);
            const teamFixtures = fixtures
              .filter((f) => f.home_team_id === t.id || f.away_team_id === t.id)
              .slice(0, 3);
            const repCount = 1 + (t.reps?.length || 0); // owner + reps
            const verifiedCount = roster.filter(
              (p) => p.hll_verified === true || p.hll_stats?.source === "scrape"
            ).length;
            const pct = roster.length ? Math.round((verifiedCount / roster.length) * 100) : 0;
            const ringColor = pct >= 80 ? "#00FFB2" : pct >= 40 ? "#F4D03F" : pct > 0 ? "#FF8C00" : "#3a3a45";
            const circumference = 2 * Math.PI * 22;
            const offset = circumference - (pct / 100) * circumference;
            return (
              <div
                key={t.id}
                data-testid={`rep-dashboard-team-${t.tag}`}
                className="border border-[#232330] bg-[#0A0A10] p-4 flex flex-col gap-3 hover:border-cyan-400 transition-colors"
                style={{ borderTopColor: t.logo_color, borderTopWidth: 3 }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: t.logo_color }}>
                      {t.role === "owner" ? "OWNER" : "REP"} · {t.tier || "Mains"}
                    </div>
                    <div className="font-heading text-2xl font-black uppercase tracking-tight" style={{ color: t.logo_color }}>
                      {t.tag}
                    </div>
                    <div className="text-xs text-gray-400 font-mono">{t.name}</div>
                  </div>
                  <div
                    className="relative w-14 h-14 flex-shrink-0"
                    data-testid={`rep-dashboard-verify-ring-${t.tag}`}
                    title={`${verifiedCount}/${roster.length} HLL-verified`}
                  >
                    <svg className="w-14 h-14 -rotate-90" viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="22" fill="none" stroke="#232330" strokeWidth="4" />
                      <circle
                        cx="25"
                        cy="25"
                        r="22"
                        fill="none"
                        stroke={ringColor}
                        strokeWidth="4"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.3s ease" }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                      <span className="font-heading font-black text-[13px]" style={{ color: ringColor }}>{pct}%</span>
                      <span className="text-[7px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">verify</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 text-center">
                  <div className="border border-[#232330] py-1">
                    <div className="text-[9px] font-mono uppercase text-gray-500">Roster</div>
                    <div className="font-heading font-bold text-white">{roster.length}<span className="text-gray-600 text-xs">/{t.roster_cap || 12}</span></div>
                  </div>
                  <div className="border border-[#232330] py-1">
                    <div className="text-[9px] font-mono uppercase text-gray-500">Reps</div>
                    <div className="font-heading font-bold text-emerald-400">{repCount}<span className="text-gray-600 text-xs">/4</span></div>
                  </div>
                  <div className="border border-[#232330] py-1">
                    <div className="text-[9px] font-mono uppercase text-gray-500">Record</div>
                    <div className="font-heading font-bold text-cyan-400">{t.wins ?? 0}-{t.losses ?? 0}</div>
                  </div>
                </div>
                {teamFixtures.length > 0 && (
                  <div className="border-t border-[#232330] pt-2 space-y-1">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500">Next matches</div>
                    {teamFixtures.map((f) => (
                      <div key={f.id} data-testid={`rep-dashboard-next-${t.tag}-${f.id}`} className="text-[10px] font-mono text-gray-400 flex justify-between">
                        <span className={f.home_team_id === t.id ? "text-white" : ""}>
                          {f.home_team_id === t.id ? "HOME" : "AWAY"}
                        </span>
                        <span>{new Date(f.scheduled_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    ))}
                  </div>
                )}
                {teamFixtures.length === 0 && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-600 text-center py-2 border-t border-[#232330]">
                    No upcoming matches
                  </div>
                )}
                <div className="flex gap-1">
                  <Link
                    to="/reps"
                    data-testid={`rep-dashboard-open-reps-${t.tag}`}
                    className="flex-1 text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 py-1.5 px-2 text-center font-bold"
                  >
                    Reps →
                  </Link>
                  <Link
                    to="/my-team"
                    data-testid={`rep-dashboard-open-team-${t.tag}`}
                    className="flex-1 text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-[#232330] hover:border-cyan-400 hover:text-cyan-400 py-1.5 px-2 text-center"
                  >
                    Manage
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Rep invites pending */}
      {stats.pendingInvites > 0 && (
        <section data-testid="rep-dashboard-invites" className="space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Rep invites</div>
          <div className="border border-[#7DF9FF]/40 bg-[#7DF9FF]/5 p-3 space-y-1">
            {invites.map((iv) => (
              <div key={iv.id} className="flex justify-between items-center">
                <span className="text-xs font-mono text-white">
                  Invited to {iv.team?.name} ({iv.team?.tag})
                </span>
                <Link to="/my-team" className="text-[10px] font-mono uppercase tracking-widest text-[#7DF9FF] hover:underline">Accept →</Link>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
